'use client';

import React, { useId } from 'react';
import clsx from 'clsx';

interface AnimatedTextProps {
  textRef: React.RefObject<SVGSVGElement | null>;
  fillMaskRef: React.RefObject<SVGRectElement | null>;
  glowRef: React.RefObject<SVGFEGaussianBlurElement | null>;
  edgeBlurRef: React.RefObject<SVGFEGaussianBlurElement | null>;
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  textRef,
  fillMaskRef,
  glowRef,
  edgeBlurRef,
}) => {
  const clipId = useId();
  const glowFilterId = useId();
  const fillBlurId = useId();
  const textString = "The Quality Collection";

  return (
    <div className="relative w-full max-w-7xl px-6 select-none dynamic-render">
      <svg
        ref={textRef}
        viewBox="0 0 1400 200"
        className="w-full h-auto will-change-transform"
        aria-label={textString}
      >
        <defs>
          {/* Clip path of the text to clamp the flowing fill */}
          <clipPath id={clipId}>
            <text
              x="50%"
              y="50%"
              dominantBaseline="middle"
              textAnchor="middle"
              className={clsx(
                "font-[900] tracking-[0.18em] fill-white"
              )}
              style={{ fontSize: 'clamp(32px, 4.8vw, 68px)' }}
            >
              {textString}
            </text>
          </clipPath>

          {/* Luxury sweeping shine gradient */}
          <linearGradient id="shine-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.9" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>

          {/* Fluid Fill edge blur filter */}
          <filter id={fillBlurId} x="-20%" y="-20%" width="1400%" height="140%">
            <feGaussianBlur ref={edgeBlurRef} stdDeviation="6 0" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Luxury ambient glow filter */}
          <filter id={glowFilterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur ref={glowRef} stdDeviation="0" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 1. Permanent elegant thin white outline */}
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className={clsx(
            "font-[900] tracking-[0.18em] fill-none stroke-white/20 stroke-[1px]"
          )}
          style={{ fontSize: 'clamp(32px, 4.8vw, 68px)' }}
        >
          {textString}
        </text>

        {/* 2. Group bound to text clipping path for liquid filling & shine */}
        <g clipPath={`url(#${clipId})`} filter={`url(#${glowFilterId})`}>
          {/* Liquid ink block filling from left-to-right */}
          <rect
            ref={fillMaskRef}
            x="-100%"
            y="0"
            width="100%"
            height="100%"
            fill="#ffffff"
            filter={`url(#${fillBlurId})`}
            className="will-change-transform"
          />

          {/* Premium Shine overlay rectangle */}
          <rect
            id="shine-overlay"
            x="-100%"
            y="0"
            width="100%"
            height="100%"
            fill="url(#shine-grad)"
            className="opacity-0 mix-blend-overlay will-change-transform"
          />
        </g>
      </svg>
    </div>
  );
};